import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { SajuResult } from '@/utils/sajuCalculations';
import { generateDeepNarrative } from '@/utils/sajuNarrativeEngine';
import { generateDeepNarrativeEn } from '@/utils/sajuNarrativeEngineEn';
import { calcSingangyak, extractYongshin } from '@/utils/singangyakCalculations';
import DongupReport from './DongupReport';

interface Props {
  result: SajuResult;
  solarYear: number;
  solarMonth: number;
  solarDay: number;
  locationName: string;
  correctedHour: number;
  correctedMinute: number;
  gender: 'male' | 'female';
  onClose: () => void;
}

type BRel = '충'|'삼합'|'방합'|'육합'|'형'|'자형'|'파'|'해'|'원진'|'귀문'|'없음';

function relColor(rels: BRel[]): string {
  if(rels.includes('충')||rels.includes('형')) return '#f87171';
  if(rels.includes('원진')&&rels.includes('귀문')) return '#c084fc';
  if(rels.includes('원진')||rels.includes('귀문')) return '#e879f9';
  if(rels.includes('파')||rels.includes('해')) return '#fb923c';
  if(rels.includes('삼합')||rels.includes('육합')||rels.includes('방합')) return '#4ade80';
  return '#94a3b8';
}

function relLabelKo(rels: BRel[]): string {
  if(rels.includes('충')) return '⚡ 충(冲)';
  if(rels.includes('삼합')) return '◎ 삼합';
  if(rels.includes('육합')) return '◎ 육합';
  if(rels.includes('방합')) return '◎ 방합';
  if(rels.includes('원진')&&rels.includes('귀문')) return '☠ 원진+귀문';
  if(rels.includes('원진')) return '⚠ 원진';
  if(rels.includes('귀문')) return '⚠ 귀문';
  if(rels.includes('파')) return '◌ 파(破)';
  if(rels.includes('해')) return '◌ 해(害)';
  return '· 없음';
}

function relLabelEn(rels: BRel[]): string {
  if(rels.includes('충')) return '⚡ Clash (冲)';
  if(rels.includes('삼합')) return '◎ Triple Harmony';
  if(rels.includes('육합')) return '◎ Six Harmony';
  if(rels.includes('방합')) return '◎ Dir. Harmony';
  if(rels.includes('원진')&&rels.includes('귀문')) return '☠ Antipathy+Ghost Gate';
  if(rels.includes('원진')) return '⚠ Antipathy';
  if(rels.includes('귀문')) return '⚠ Ghost Gate';
  if(rels.includes('파')) return '◌ Breach (破)';
  if(rels.includes('해')) return '◌ Harm (害)';
  return '· None';
}

function ScoreBar({ score, isEn }: { score: number; isEn: boolean }) {
  const c = score >= 75 ? '#f59e0b' : score >= 58 ? '#4ade80' : score >= 44 ? '#94a3b8' : '#f87171';
  const lbl = isEn
    ? (score >= 75 ? 'Great' : score >= 58 ? 'Good' : score >= 44 ? 'OK' : 'Care')
    : (score >= 75 ? '길(吉)' : score >= 58 ? '평길' : score >= 44 ? '평(平)' : '주의');
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width:`${score}%`, backgroundColor:c }} />
      </div>
      <span className="text-[10px] font-black w-10 text-right" style={{color:c}}>{lbl}</span>
    </div>
  );
}

function Section({ num, title }: { num: string; title: string }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <span className="text-[10px] text-amber-400/40 font-mono tracking-widest whitespace-nowrap">§ {num}</span>
      <div className="h-px flex-1 bg-white/8" />
      <h3 className="text-sm font-black text-white/90 whitespace-nowrap tracking-wide">{title}</h3>
    </div>
  );
}

function Tag({ text, color }: { text: string; color: string }) {
  return (
    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm border" style={{ color, borderColor:`${color}40`, background:`${color}15` }}>
      {text}
    </span>
  );
}

function MonthRow({ m, isEn }: { m: ReturnType<typeof generateDeepNarrative>['months'][0]; isEn: boolean }) {
  const [open, setOpen] = useState(false);
  const sc = m.score >= 75 ? '#f59e0b' : m.score >= 58 ? '#4ade80' : m.score >= 44 ? '#94a3b8' : '#f87171';
  const lbl = isEn
    ? (m.score >= 75 ? 'Great' : m.score >= 58 ? 'Good' : m.score >= 44 ? 'Fair' : 'Care')
    : (m.score >= 75 ? '길' : m.score >= 58 ? '평길' : m.score >= 44 ? '평' : '주의');

  const titleParts = m.title.split('—');
  const subTitle = titleParts[1]?.trim() || m.title;

  return (
    <button onClick={() => setOpen(!open)} className="w-full text-left border-b border-white/5 py-3.5 cursor-pointer group">
      <div className="flex items-start gap-3">
        <div className="w-14 flex-none">
          <p className="text-sm font-black text-white leading-none">{m.solarMonth}</p>
          <p className="text-[10px] text-white/25 font-mono mt-0.5">{m.ganji}</p>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-1 mb-1.5">
            {m.tags.slice(0, 3).map((t, i) => (
              <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded-sm ${
                i===0 ? 'bg-amber-400/10 text-amber-400/80' : t.includes('충')||t.includes('Clash') ? 'text-red-400/70 bg-red-500/10' : t.includes('합')||t.includes('Harmony') ? 'text-green-400/70 bg-green-500/10' : (t.includes('파')||t.includes('원진')||t.includes('귀문')||t.includes('Breach')||t.includes('Antipathy')) ? 'text-orange-400/70 bg-orange-500/10' : 'text-white/30 bg-white/5'
              }`}>{t}</span>
            ))}
          </div>
          <p className="text-xs text-white/65 leading-tight">{subTitle}</p>
          <div className="mt-2">
            <ScoreBar score={m.score} isEn={isEn} />
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-none pt-0.5">
          <span className="text-xs font-black" style={{ color: sc }}>{lbl}</span>
          <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-white/20 text-base`} />
        </div>
      </div>
      {open && (
        <div className="mt-3 ml-12 sm:ml-[68px]">
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2">
            <p className="text-xs font-bold text-white/80 leading-relaxed">{titleParts[1]?.trim() || m.title}</p>
            <p className="text-xs text-white/50 leading-relaxed">{m.detail}</p>
            <div className="flex items-center gap-2 pt-1 border-t border-white/5 flex-wrap">
              {m.tags.map((t, i) => (
                <span key={i} className="text-[10px] text-white/30 font-mono">{t}</span>
              ))}
              <span className="text-[10px] font-black ml-auto" style={{ color: sc }}>
                {isEn ? `Score ${m.score}` : `세운지수 ${m.score}`}
              </span>
            </div>
          </div>
        </div>
      )}
    </button>
  );
}

const REPORT_YEAR = 2026;
const REPORT_GANJI_KO = '병오(丙午)년';
const REPORT_GANJI_EN = 'Bǐngwǔ (丙午)';

export default function YearlyFortuneReport({
  result, solarYear, solarMonth, solarDay, onClose,
}: Props) {
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const [stage, setStage] = useState<'cover' | 'report'>('cover');
  const [activeTab, setActiveTab] = useState<'overview'|'detail'|'monthly'>('overview');
  const [showDongup, setShowDongup] = useState(false);
  const [shared, setShared] = useState(false);
  const navigate = useNavigate();

  const nar = isEn ? generateDeepNarrativeEn(result) : generateDeepNarrative(result);
  const singangyak = calcSingangyak(result);
  const yongshin = extractYongshin(result, singangyak);

  const reportId = `BSAJU-${solarYear}${String(solarMonth).padStart(2,'0')}${String(solarDay).padStart(2,'0')}-${result.dayPillar.stem}${result.dayPillar.branch}`;
  const birthStr = `${solarYear}.${String(solarMonth).padStart(2,'0')}.${String(solarDay).padStart(2,'0')}`;
  const sc = nar.overallScore >= 75 ? '#f59e0b' : nar.overallScore >= 60 ? '#4ade80' : nar.overallScore >= 45 ? '#94a3b8' : '#f87171';
  const scLbl = isEn
    ? (nar.overallScore >= 75 ? 'Excellent (大吉)' : nar.overallScore >= 60 ? 'Auspicious (吉)' : nar.overallScore >= 45 ? 'Neutral (平)' : 'Caution (注意)')
    : (nar.overallScore >= 75 ? '대길(大吉)' : nar.overallScore >= 60 ? '길(吉)' : nar.overallScore >= 45 ? '평(平)' : '주의(注意)');

  const reportGanji = isEn ? REPORT_GANJI_EN : REPORT_GANJI_KO;

  const handleShare = async () => {
    const shareText = isEn
      ? `🔒 [CLASSIFIED] BarunSaju 2026 Fortune Report\n\nDay Pillar: ${nar.dayPillarLabel}\n2026 Annual God: ${nar.seunSipseong}\nOverall Score: ${nar.overallScore} (${scLbl})\n\nAnalyze your own chart 👉 ${window.location.href}`
      : `🔒 [CLASSIFIED] 바른사주 VIP 신년운세\n\n일주: ${nar.dayPillarLabel}\n2026년 세운: ${nar.seunSipseong}\n종합 세운 지수: ${nar.overallScore}점 (${scLbl})\n\n나도 내 사주 분석하러 가기 👉 ${window.location.href}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: isEn ? 'BarunSaju 2026 Annual Fortune Report' : '바른사주 VIP 신년운세 리포트',
          text: shareText,
          url: window.location.href,
        });
        setShared(true);
        setTimeout(() => setShared(false), 2500);
        return;
      } catch { /* cancelled */ }
    }
    try { await navigator.clipboard.writeText(shareText); }
    catch {
      const ta = document.createElement('textarea');
      ta.value = shareText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setShared(true);
    setTimeout(() => setShared(false), 2500);
  };

  // ── Cover ─────────────────────────────────────────────────────
  if (stage === 'cover') {
    return (
      <div className="fixed inset-0 z-50 bg-[#060606] flex flex-col min-h-dvh overflow-hidden">
        <button
          onClick={onClose}
          className="absolute z-10 w-9 h-9 flex items-center justify-center rounded-full border border-white/10 text-white/40 hover:text-white/70 transition-colors cursor-pointer"
          style={{ top: 'max(16px, env(safe-area-inset-top))', right: 'max(16px, env(safe-area-inset-right))' }}
        >
          <i className="ri-close-line text-base" />
        </button>
        <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 md:py-12 overflow-y-auto"
          style={{ paddingTop: 'max(32px, env(safe-area-inset-top))' }}
        >
          <div className="flex items-center gap-2 mb-8">
            <div className="h-px flex-1 bg-amber-400/20" />
            <div className="flex items-center gap-1.5 border border-amber-400/40 px-3 py-1 rounded-sm">
              <i className="ri-lock-line text-amber-400 text-xs" />
              <span className="text-amber-400 text-[10px] font-black tracking-[4px]">CLASSIFIED</span>
            </div>
            <div className="h-px flex-1 bg-amber-400/20" />
          </div>
          <p className="text-white/20 text-xs tracking-[6px] font-light mb-1">
            {isEn ? 'BARUNSAJU VIP DESTINY ANALYSIS' : '바른사주 VIP 운명분석국'}
          </p>
          <p className="text-white/10 text-[10px] font-mono mb-10">{reportId}</p>
          <div className="text-center mb-8">
            <div className="inline-block border border-amber-400/25 px-4 py-1.5 mb-4">
              <p className="text-amber-400/80 text-xs tracking-wider">{nar.dayPillarLabel}</p>
            </div>
            <p className="text-white/30 text-[11px] font-mono mb-3">{nar.pillarsStr}</p>
            <div className="inline-block bg-amber-400/8 border border-amber-400/30 px-6 py-2.5 rounded-sm mb-5">
              <p className="text-amber-400 text-xl font-black tracking-wider" style={{ fontFamily:"'Noto Serif KR', serif" }}>
                {nar.seunSipseong} {isEn ? '' : '세운'}
              </p>
            </div>
            <h1 className="text-4xl font-black leading-tight mb-2" style={{ color:'#f59e0b', fontFamily:"'Noto Serif KR', serif", textShadow:'0 0 50px rgba(245,158,11,0.25)' }}>
              {REPORT_YEAR}<br />{reportGanji}
            </h1>
            <h2 className="text-base font-bold text-white/80">
              {isEn ? 'In-Depth Destiny Analysis Report' : '심층 운명 분석 리포트'}
            </h2>
          </div>
          <div className="bg-white/4 border border-white/8 rounded-lg px-5 py-3 mb-10 text-center">
            <p className="text-white/35 text-[11px] leading-relaxed font-mono">
              {isEn
                ? 'True Solar Time Corrected · Birthplace Longitude Applied\n4 Pillars · Seasonal Harmony · Stem Combo · Antipathy/Ghost Gate'
                : '진태양시 보정 완료 · 출생지 경도 반영\n4주(年月日時) 전체 분석 · 조후·천간합·원진귀문 반영'}
            </p>
          </div>
          <button
            onClick={() => setStage('report')}
            className="group flex items-center gap-3 border border-amber-400/50 hover:border-amber-400 bg-amber-400/5 hover:bg-amber-400/10 px-8 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-paper-2-line text-amber-400 text-base" />
            <span className="text-amber-400 font-black tracking-wider text-sm">
              {isEn ? 'Open Report' : '리포트 열기'}
            </span>
            <i className="ri-arrow-right-line text-amber-400/70 text-sm group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
        <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />
        <p
          className="text-center text-white/10 text-[10px] font-mono pt-2"
          style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
        >
          CONFIDENTIAL · FOR RECIPIENT ONLY · {birthStr}
        </p>
      </div>
    );
  }

  // ── Report Body ──────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 bg-[#080808] overflow-y-auto">
      {/* Header */}
      <div
        className="sticky top-0 z-10 bg-[#080808]/96 backdrop-blur border-b border-white/8 px-4 flex items-center gap-3"
        style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}
      >
        <div className="flex items-center gap-1.5">
          <i className="ri-lock-line text-amber-400 text-xs" />
          <p className="text-amber-400 text-[10px] tracking-[3px] font-bold">CLASSIFIED</p>
        </div>
        <p className="text-white/15 text-[10px] font-mono truncate flex-1">{reportId}</p>
        <button
          onClick={handleShare}
          className={`w-7 h-7 flex items-center justify-center border rounded transition-all cursor-pointer flex-none ${shared ? 'border-amber-400/50 bg-amber-400/10' : 'border-white/10 hover:border-amber-400/40 hover:bg-amber-400/5'}`}
        >
          {shared
            ? <i className="ri-check-line text-amber-400 text-sm" />
            : <i className="ri-share-forward-line text-white/40 text-sm hover:text-amber-400/70" />
          }
        </button>
        <button onClick={onClose} className="w-7 h-7 flex items-center justify-center border border-white/10 rounded text-white/40 hover:text-white/70 cursor-pointer transition-colors flex-none">
          <i className="ri-close-line text-sm" />
        </button>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Title + Score card */}
        <div className="mb-8">
          <p className="text-white/25 text-xs font-mono mb-1">{birthStr} · {nar.pillarsStr}</p>
          <h2 className="text-xl font-black text-white mb-0.5" style={{ fontFamily:"'Noto Serif KR', serif" }}>
            {REPORT_YEAR} {reportGanji}
          </h2>
          <p className="text-amber-400/70 text-sm mb-4">{nar.dayPillarLabel}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/4 border border-white/10 rounded-xl p-4">
              <p className="text-white/30 text-[10px] mb-1">
                {isEn ? 'Annual Fortune Score' : '종합 세운 지수'}
              </p>
              <p className="text-3xl font-black mb-0.5" style={{ color: sc }}>{nar.overallScore}</p>
              <p className="text-xs font-black" style={{ color: sc }}>{scLbl}</p>
            </div>
            <div className="bg-amber-400/5 border border-amber-400/20 rounded-xl p-4">
              <p className="text-white/30 text-[10px] mb-1">
                {isEn ? 'Annual Ten God' : '세운 십신'}
              </p>
              <p className="text-2xl font-black text-amber-400 mb-0.5" style={{ fontFamily:"'Noto Serif KR', serif" }}>
                {nar.seunSipseong}
              </p>
              <p className="text-[10px] text-amber-400/50">
                {isEn ? 'Bǐnghuǒ (丙火) · 2026' : '병화(丙火) · 2026'}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex bg-white/5 rounded-xl p-1 mb-6">
          {(isEn
            ? [['overview','📋 Overview'],['detail','🔍 By Domain'],['monthly','📅 Monthly']] as [string,string][]
            : [['overview','📋 총론·4주분석'],['detail','🔍 분야별'],['monthly','📅 월별']] as [string,string][]
          ).map(([key,lbl])=>(
            <button key={key} onClick={()=>setActiveTab(key as typeof activeTab)}
              className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${activeTab===key ? 'bg-amber-400/20 text-amber-400' : 'text-white/40 hover:text-white/60'}`}>
              {lbl}
            </button>
          ))}
        </div>

        {/* ── Tab 1: Overview + 4 Pillars ───────────────────── */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Overview */}
            <div>
              <Section num="01" title={isEn ? 'Overview (總論)' : '총론(總論)'} />
              <div className="bg-amber-400/4 border border-amber-400/20 rounded-xl p-5 mb-4">
                <p className="text-amber-400/80 text-sm font-black mb-4 leading-snug" style={{ fontFamily:"'Noto Serif KR', serif" }}>
                  {nar.overviewTitle}
                </p>
                <div className="space-y-3">
                  {[nar.overviewP1, nar.overviewP2, nar.overviewP3].map((p, i) => (
                    <p key={i} className="text-sm text-white/65 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: p.replace(/\*\*(.+?)\*\*/g, '<strong class="text-amber-300">$1</strong>') }} />
                  ))}
                </div>
              </div>
              <div className="bg-white/4 border border-white/10 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-sword-line text-amber-400 text-xs" />
                  <p className="text-xs font-black text-white/80">
                    {isEn ? '2026 Core Strategy' : '2026년 핵심 전략'}
                  </p>
                </div>
                <p className="text-sm text-white/55 leading-relaxed">{nar.strategy}</p>
              </div>
            </div>

            {/* 4 Pillars Branch Analysis */}
            <div>
              <Section num="02" title={isEn ? '4 Pillars (四柱) Branch Analysis' : '4주(四柱) 지지 분석'} />
              <p className="text-xs text-white/35 mb-4 leading-relaxed">
                {isEn
                  ? 'Analysis of how each pillar (Year/Month/Day/Hour) relates to the 2026 annual branch Wǔ (午). Clash = upheaval · Harmony = cohesion · Breach/Harm/Antipathy/Ghost Gate = internal friction.'
                  : '각 기둥(年·月·日·時)이 2026년 세운지 午(오)와 어떤 관계를 맺는지 분석합니다. 충(冲)은 격변, 합(合)은 응집, 파(破)·해(害)·원진·귀문은 내면적 마찰을 뜻합니다.'}
              </p>
              {nar.pillarAnalysis.length === 0 && (
                <div className="border border-white/10 rounded-xl p-5 text-center">
                  <p className="text-white/30 text-sm">
                    {isEn
                      ? 'No significant relationships between your 4 Pillars and the annual Wǔ (午) branch.'
                      : '4주 지지와 세운지 午(오) 사이에 특별한 관계가 없습니다.'}
                  </p>
                  <p className="text-white/20 text-xs mt-1">
                    {isEn ? 'A relatively stable year overall.' : '상대적으로 안정적인 한 해입니다.'}
                  </p>
                </div>
              )}
              <div className="space-y-3">
                {nar.pillarAnalysis.map((p, i) => {
                  const col = relColor(p.rels as BRel[]);
                  const lbl = isEn ? relLabelEn(p.rels as BRel[]) : relLabelKo(p.rels as BRel[]);
                  return (
                    <div key={i} className="border rounded-xl p-4" style={{ borderColor:`${col}30`, background:`${col}05` }}>
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <span className="text-xs font-black text-white/80">{p.label}</span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border" style={{ color:col, borderColor:`${col}40`, background:`${col}15` }}>
                          {lbl}
                        </span>
                        <span className="text-[10px] text-white/30 font-mono ml-auto">{p.relStr}</span>
                      </div>
                      <p className="text-xs text-white/55 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: p.text.replace(/\*\*(.+?)\*\*/g, `<strong style="color:${col}">$1</strong>`) }} />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Beneficial Element Guide */}
            <div>
              <Section num="03" title={isEn ? 'Beneficial Element (用神) Guide' : '용신(用神) 가이드'} />
              <div className="bg-white/4 border border-white/10 rounded-xl p-5 mb-3">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-amber-400/10 border border-amber-400/25 rounded-xl flex-none">
                    <i className="ri-sparkling-2-line text-amber-400 text-sm" />
                  </div>
                  <div>
                    <p className="text-[10px] text-white/25 font-mono">
                      {isEn ? '2026 Core Beneficial Element' : '2026년 핵심 용신'}
                    </p>
                    <p className="text-sm font-bold text-white">
                      {yongshin.element} {isEn ? 'Element (用神)' : '기운 (用神)'}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-white/40 leading-relaxed mb-3">{yongshin.reason}</p>
                <p className="text-xs text-white/55 leading-relaxed">{yongshin.tossMessage}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {yongshin.items.slice(0, 4).map((item, i) => (
                  <div key={i} className="border border-white/8 rounded-xl p-3">
                    <p className="text-[10px] text-amber-400/50 mb-2 font-mono">{item.category}</p>
                    <div className="flex flex-wrap gap-1">
                      {item.examples.map((ex, j) => (
                        <span key={j} className="text-[10px] text-white/45 bg-white/5 px-1.5 py-0.5 rounded-sm">{ex}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Tab 2: Domain Analysis ───────────────────────── */}
        {activeTab === 'detail' && (
          <div className="space-y-4">
            <Section num="04" title={isEn ? 'In-Depth Fortune by Domain' : '분야별 심층 운세'} />
            <p className="text-xs text-white/30 -mt-3 mb-2 leading-relaxed">
              {isEn
                ? 'Each domain is analyzed based on the relationships between your 4 Pillars and the 2026 annual branch.'
                : '4주 지지와 세운의 관계를 기반으로 각 분야별 구체적 의미를 분석합니다.'}
            </p>
            {[
              { icon:'ri-briefcase-4-line', label: isEn ? 'Career · Business · Growth' : '직장·사업·커리어', emoji:'💼', text: nar.careerText },
              { icon:'ri-coins-line', label: isEn ? 'Wealth · Finance · Investment' : '재물·금전·투자', emoji:'💰', text: nar.moneyText },
              { icon:'ri-heart-line', label: isEn ? 'Relationships · Romance · People' : '이성·대인관계', emoji:'❤️', text: nar.loveText },
              { icon:'ri-heart-pulse-line', label: isEn ? 'Health · Home · Inner World' : '건강·가정·내면', emoji:'🏡', text: nar.healthText },
            ].map((item, i) => (
              <div key={i} className="border border-white/10 rounded-xl overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 bg-white/3 border-b border-white/8">
                  <i className={`${item.icon} text-amber-400 text-base`} />
                  <p className="text-sm font-bold text-white">{item.label}</p>
                </div>
                <div className="p-4">
                  <p className="text-sm text-white/60 leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Tab 3: Monthly Fortune ───────────────────────── */}
        {activeTab === 'monthly' && (
          <div>
            <Section num="05" title={isEn ? '12-Month Fortune Flow' : '12개월 세운 흐름'} />
            <p className="text-xs text-white/30 -mt-3 mb-4 leading-relaxed">
              {isEn
                ? "Monthly analysis of how each month's Heavenly Stem and Earthly Branch interact with your chart. Clash · Harmony · Breach · Harm · Antipathy · Ghost Gate · Stem Combo all included."
                : '각 달의 천간·지지가 원국 4주와 어떤 관계를 맺는지 분석합니다. 충(冲)·합(合)·파(破)·해·원진·귀문·천간합이 모두 반영되었습니다.'}
            </p>
            {/* Monthly score preview */}
            <div className="grid grid-cols-6 gap-1 mb-4">
              {nar.months.map(m => {
                const c = m.score >= 75 ? '#f59e0b' : m.score >= 58 ? '#4ade80' : m.score >= 44 ? '#94a3b8' : '#f87171';
                return (
                  <div key={m.month} className="flex flex-col items-center gap-0.5">
                    <div className="w-full h-8 rounded-sm relative overflow-hidden bg-white/5">
                      <div className="absolute bottom-0 w-full rounded-sm transition-all" style={{ height:`${m.score}%`, backgroundColor:`${c}50` }} />
                    </div>
                    <span className="text-[9px] text-white/30">{m.solarMonth}</span>
                  </div>
                );
              })}
            </div>
            <div className="border border-white/10 rounded-xl overflow-hidden">
              <div className="px-4 py-2 border-b border-white/5 bg-white/3 grid grid-cols-[56px_1fr_48px] gap-2">
                <p className="text-[10px] text-white/20 font-mono">
                  {isEn ? 'Month' : '월/간지'}
                </p>
                <p className="text-[10px] text-white/20 font-mono">
                  {isEn ? 'Summary' : '요약'}
                </p>
                <p className="text-[10px] text-white/20 font-mono text-right">
                  {isEn ? 'Score' : '판정'}
                </p>
              </div>
              <div className="px-4">
                {nar.months.map(m => <MonthRow key={m.month} m={m} isEn={isEn} />)}
              </div>
            </div>
            <div className="mt-3 flex items-start gap-2 bg-amber-400/4 border border-amber-400/15 rounded-xl p-3">
              <i className="ri-information-line text-amber-400/60 text-xs mt-0.5 flex-none" />
              <p className="text-[10px] text-amber-400/50 leading-relaxed">
                {isEn
                  ? 'Monthly scores reflect the combined interactions of each month\'s Heavenly Stem and Earthly Branch against all 4 Pillars and the annual branch Wǔ. Clash · Harmony · Breach · Harm · Antipathy · Ghost Gate · Stem Combo are all included.'
                  : '월별 분석은 해당 월의 간지가 원국 4주(年月日時) 및 세운지 午와 형성하는 관계를 종합한 값입니다. 충·합·파·해·원진·귀문·천간합이 모두 반영되었습니다.'}
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="pt-8" style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}>
          <div className="h-px bg-white/8 mb-5" />
          <p className="text-center text-white/10 text-[10px] font-mono mb-5">
            END OF REPORT · {reportId}
          </p>

          {/* CTA Links */}
          <div className="bg-white/3 border border-white/10 rounded-2xl p-4 mb-4">
            <p className="text-[10px] text-white/25 font-mono mb-3 text-center tracking-wider">
              {isEn ? 'NEXT ANALYSIS · Explore More' : 'NEXT ANALYSIS · 더 알아보기'}
            </p>
            <div className="space-y-2.5">
              <button
                onClick={() => setShowDongup(true)}
                className="w-full flex items-center gap-3 bg-amber-400/8 hover:bg-amber-400/15 border border-amber-400/30 hover:border-amber-400/50 rounded-xl px-4 py-3.5 transition-all cursor-pointer group"
              >
                <div className="w-9 h-9 flex items-center justify-center bg-amber-400/10 rounded-lg flex-none">
                  <i className="ri-team-line text-amber-400 text-base" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-black text-white/85">
                    {isEn ? '🤝 Business Partner Compatibility' : '🤝 동업·파트너 궁합 분석'}
                  </p>
                  <p className="text-[10px] text-white/35">
                    {isEn ? 'Day Branch Clash/Harmony · R&R · 2026 Synergy · Risk Analysis' : '일지 합충 · R&R · 2026 시너지 · 리스크 팩폭'}
                  </p>
                </div>
                <i className="ri-arrow-right-line text-amber-400/50 text-sm group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={() => { onClose(); navigate('/goonghap'); }}
                className="w-full flex items-center gap-3 bg-white/4 hover:bg-white/8 border border-white/10 hover:border-white/20 rounded-xl px-4 py-3.5 transition-all cursor-pointer group"
              >
                <div className="w-9 h-9 flex items-center justify-center bg-rose-500/10 rounded-lg flex-none">
                  <i className="ri-heart-3-line text-rose-400 text-base" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-black text-white/75">
                    {isEn ? '💕 Romantic Compatibility Analysis' : '💕 연애·부부 궁합 보러가기'}
                  </p>
                  <p className="text-[10px] text-white/35">
                    {isEn ? 'Cross-analysis of two complete charts' : '두 사람의 사주 정밀 교차 분석'}
                  </p>
                </div>
                <i className="ri-arrow-right-line text-white/20 text-sm group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          <button onClick={onClose} className="w-full border border-white/15 text-white/40 hover:text-white/65 hover:border-white/25 py-3.5 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap">
            {isEn ? 'Back to Chart Result' : '사주 결과로 돌아가기'}
          </button>
        </div>
      </div>

      {/* Partner analysis overlay */}
      {showDongup && (
        <DongupReport
          userResult={result}
          initialNameA=""
          onBack={() => setShowDongup(false)}
          onClose={onClose}
        />
      )}
    </div>
  );
}
